import { motion } from 'framer-motion';
import { Zap, Lock, TrendingUp, Edit3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useSubscription } from '@/hooks/useSubscription';
import { cn } from '@/lib/utils';
import { formatLimit } from '@/lib/plan-limits';
import { useCallback } from 'react';

interface DynamicToggleProps {
  isDynamic: boolean;
  onChange: (isDynamic: boolean) => void;
}

export function DynamicToggle({ 
  isDynamic, 
  onChange, 
}: DynamicToggleProps) {
  const { usage, limits, isAtDynamicQRLimit } = useSubscription();
  const dynamicUsed = usage?.dynamic_qr_count || 0;
  const dynamicLimit = limits.dynamicQRLimit;
  const isAtLimit = isAtDynamicQRLimit();

  // Radix can invoke onValueChange during controlled mounts/updates.
  // Guarding prevents a render -> state update -> render loop when the value is unchanged.
  const handleValueChange = useCallback(
    (value: string) => {
      const nextIsDynamic = value === 'dynamic';
      if (nextIsDynamic !== isDynamic) onChange(nextIsDynamic);
    },
    [isDynamic, onChange]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          QR Code Type
        </h3>
        {dynamicLimit !== -1 && (
          <Badge variant="outline" className="text-xs">
            {dynamicUsed}/{formatLimit(dynamicLimit)} dynamic used
          </Badge>
        )}
      </div>

      <RadioGroup
        value={isDynamic ? 'dynamic' : 'static'}
        onValueChange={handleValueChange}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        {/* Static Option */}
        <Label
          htmlFor="static"
          className={cn(
            'cursor-pointer flex flex-col items-start p-3 sm:p-4 rounded-xl border-2 transition-all min-w-0',
            !isDynamic
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-muted-foreground/50'
          )}
        >
          <div className="flex items-center gap-2 w-full min-w-0">
            <RadioGroupItem value="static" id="static" className="sr-only" />
            <div
              className={cn(
                'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                !isDynamic ? 'border-primary' : 'border-muted-foreground'
              )}
            >
              {!isDynamic && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-2 h-2 rounded-full bg-primary"
                />
              )}
            </div>
            <Lock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <span className="font-medium truncate">Static</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2 ml-6 line-clamp-2">
            Data encoded directly in QR. Cannot be edited after creation.
          </p>
          <div className="flex items-center gap-1.5 mt-2 ml-6 flex-wrap">
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              Free
            </Badge>
            <span className="text-[10px] text-muted-foreground">Unlimited</span>
          </div>
        </Label>

        {/* Dynamic Option */}
        <Label
          htmlFor="dynamic"
          className={cn(
            'cursor-pointer flex flex-col items-start p-3 sm:p-4 rounded-xl border-2 transition-all min-w-0',
            isDynamic
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-muted-foreground/50',
            isAtLimit && !isDynamic && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="flex items-center gap-2 w-full min-w-0">
            <RadioGroupItem 
              value="dynamic" 
              id="dynamic" 
              className="sr-only" 
              disabled={isAtLimit && !isDynamic}
            />
            <div
              className={cn(
                'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                isDynamic ? 'border-primary' : 'border-muted-foreground'
              )}
            >
              {isDynamic && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-2 h-2 rounded-full bg-primary"
                />
              )}
            </div>
            <Zap className="h-4 w-4 text-secondary flex-shrink-0" />
            <span className="font-medium truncate">Dynamic</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2 ml-6 line-clamp-2">
            Editable destination. Track scans and analytics.
          </p>
          <div className="flex items-center gap-2 sm:gap-3 mt-2 ml-6 flex-wrap">
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Edit3 className="h-3 w-3 flex-shrink-0" />
              <span>Editable</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <TrendingUp className="h-3 w-3 flex-shrink-0" />
              <span>Tracked</span>
            </div>
          </div>
        </Label>
      </RadioGroup>

      {isAtLimit && !isDynamic && (
        <p className="text-xs text-destructive">
          You've reached your dynamic QR limit.{' '}
          <Link to="/pricing" className="underline font-medium hover:text-destructive/80">
            Upgrade your plan
          </Link>{' '}
          for more.
        </p>
      )}
    </div>
  );
}
